// The dashboard editor's grid rules (WEB-DSH-EDIT-01, DSH-02, DSH-V-03, DDB-30 … DDB-34): widgets stay inside the 12
// columns, never overlap, a moved or resized widget pushes the ones it lands on out of its way rather than being
// refused, and gravity then moves every widget up as far as it can, so no row or hole is left empty.
import type { DashboardDocument } from "./copy";

export type DocWidget = DashboardDocument["widgets"][number];
type Pos = DocWidget["position"];

export const COLUMNS = 12;
/** The `h` limit of dashboard.json (DDB-32). */
export const MAX_ROWS = 12;

/** The smallest size of a widget per type (DDB-32). */
export function minSize(type: string): { w: number; h: number } {
  switch (type) {
    case "value":
    case "toggle":
    case "slider":
    case "scene_button":
    case "text":
      return { w: 2, h: 1 };
    case "gauge":
    case "compass":
    case "level":
    case "stats":
    case "period_totals":
    case "period_compare":
    case "weather_station":
    case "status_list":
    case "alarm_list":
    case "schedule":
      return { w: 3, h: 2 };
    case "state_timeline":
      return { w: 4, h: 2 };
    case "line_chart":
    case "bar_chart":
    case "map":
    case "heatmap":
    case "health":
    case "year_compare":
      return { w: 4, h: 3 };
    case "web_widget":
      return { w: 12, h: 3 };
    default:
      return { w: 2, h: 2 };
  }
}

/** The tallest a widget of the type may be: a web_widget 8 rows (mobile-dashboards-2 MD2-03), others MAX_ROWS. */
export function maxRows(type: string): number {
  return type === "web_widget" ? 8 : MAX_ROWS;
}

/** The Size choices of the widget menu (DDB-33): the type's minimum, its default, double height, full width. */
export function sizePreset(type: string, preset: "small" | "medium" | "large" | "full"): { w: number; h: number } {
  const d = defaultSize(type);
  if (preset === "small") return minSize(type);
  if (preset === "large") return { w: d.w, h: Math.min(maxRows(type), d.h * 2) };
  if (preset === "full") return { w: COLUMNS, h: d.h };
  return d;
}

/** The default size of a new widget per type: the device-dashboards §5 table the backend's generator uses. */
export function defaultSize(type: string): { w: number; h: number } {
  switch (type) {
    case "value_grid":
      return { w: 12, h: 2 };
    case "line_chart":
    case "bar_chart":
      return { w: 12, h: 4 };
    case "map":
      return { w: 12, h: 5 };
    case "status_list":
    case "alarm_list":
    case "schedule":
      return { w: 12, h: 3 };
    case "text":
    case "image":
    case "insight_card":
      return { w: 6, h: 2 };
    case "device_tile":
    case "sparkline":
    case "reset_counter":
      return { w: 3, h: 2 };
    case "state_timeline":
    case "period_totals":
      return { w: 6, h: 2 };
    case "heatmap":
    case "health":
    case "year_compare":
      return { w: 6, h: 3 };
    case "web_widget":
      return { w: 12, h: 4 };
    default:
      return { w: 4, h: 2 };
  }
}

export function overlaps(a: Pos, b: Pos): boolean {
  return a.x < b.x + b.w && b.x < a.x + a.w && a.y < b.y + b.h && b.y < a.y + a.h;
}

/** DSH-V-03 before saving: every widget inside the grid and none overlapping. */
export function layoutProblems(widgets: DocWidget[]): string[] {
  const out: string[] = [];
  widgets.forEach((w, i) => {
    const p = w.position;
    if (p.x < 0 || p.y < 0 || p.w < 1 || p.h < 1 || p.x + p.w > COLUMNS) out.push(w.id);
    else if (widgets.slice(0, i).some((o) => overlaps(o.position, p))) out.push(w.id);
  });
  return out;
}

/** A position clamped into the grid: at least the type's minimum, at most the width and 12 rows, never left of 0. */
export function clamp(p: Pos, type = ""): Pos {
  const min = minSize(type);
  const w = Math.min(COLUMNS, Math.max(min.w, Math.round(p.w)));
  const h = Math.min(maxRows(type), Math.max(min.h, Math.round(p.h)));
  return { x: Math.min(COLUMNS - w, Math.max(0, Math.round(p.x))), y: Math.max(0, Math.round(p.y)), w, h };
}

/**
 * Gravity (DDB-30): in reading order, each widget moves straight up until it touches a widget above or row 0. Columns
 * and sizes never change. The result keeps the input's order.
 */
export function compact<T extends { id: string; position: Pos }>(widgets: T[]): T[] {
  const done: Pos[] = [];
  const at = new Map<string, Pos>();
  for (const w of [...widgets].sort((a, b) => a.position.y - b.position.y || a.position.x - b.position.x)) {
    let p = { ...w.position, y: Math.max(0, w.position.y) };
    while (p.y > 0 && !done.some((q) => overlaps(q, { ...p, y: p.y - 1 }))) p = { ...p, y: p.y - 1 };
    done.push(p);
    at.set(w.id, p);
  }
  return widgets.map((w) => {
    const p = at.get(w.id);
    return !p || (p.x === w.position.x && p.y === w.position.y) ? w : { ...w, position: p };
  });
}

/**
 * Puts one widget at a position (clamped), moves the widgets it lands on out of its way (into the place it left when
 * they fit there, so the two swap; else above it when they fit; else below it), then applies gravity.
 */
export function place(widgets: DocWidget[], id: string, to: Pos): DocWidget[] {
  const moved = widgets.find((w) => w.id === id);
  if (!moved) return widgets;
  const target = clamp(to, moved.type);
  const fixed: DocWidget[] = [{ ...moved, position: target }];
  const rest = widgets
    .filter((w) => w.id !== id)
    .sort((a, b) => a.position.y - b.position.y || a.position.x - b.position.x);
  const free = (p: Pos, self: string) =>
    !fixed.some((f) => overlaps(f.position, p)) &&
    !rest.some((r) => r.id !== self && !fixed.includes(r) && overlaps(r.position, p));
  for (const w of rest) {
    let pos = { ...w.position };
    if (overlaps(target, pos)) {
      const back = { ...pos, x: Math.min(COLUMNS - pos.w, moved.position.x), y: moved.position.y };
      const above = { ...pos, y: target.y - pos.h };
      if (free(back, w.id)) pos = back;
      else if (above.y >= 0 && free(above, w.id)) pos = above;
    }
    for (let guard = 0; guard < 1000; guard++) {
      const hit = fixed.find((f) => overlaps(f.position, pos));
      if (!hit) break;
      pos = { ...pos, y: hit.position.y + hit.position.h };
    }
    fixed.push({ ...w, position: pos });
  }
  // Keep the document's own order, so ids and indexes in errors stay where the user put them.
  return compact(widgets.map((w) => fixed.find((f) => f.id === w.id) ?? w));
}

export function move(widgets: DocWidget[], id: string, dx: number, dy: number): DocWidget[] {
  const w = widgets.find((x) => x.id === id);
  return w ? place(widgets, id, { ...w.position, x: w.position.x + dx, y: w.position.y + dy }) : widgets;
}

/**
 * One keyboard step (DDB-33): left and right by a column; up and down past the nearest widget above or below in the
 * same columns, so the two swap, since with gravity a single row down would fall straight back.
 */
export function step(widgets: DocWidget[], id: string, dx: number, dy: number): DocWidget[] {
  const w = widgets.find((x) => x.id === id);
  if (!w || dy === 0) return w ? move(widgets, id, dx, 0) : widgets;
  const p = w.position;
  const cols = (q: Pos) => q.x < p.x + p.w && p.x < q.x + q.w;
  const others = widgets.filter((o) => o.id !== id && cols(o.position));
  const next =
    dy > 0
      ? others.filter((o) => o.position.y >= p.y + p.h).sort((a, b) => a.position.y - b.position.y)[0]
      : others.filter((o) => o.position.y + o.position.h <= p.y).sort((a, b) => b.position.y - a.position.y)[0];
  if (!next) return widgets;
  return place(widgets, id, { ...p, y: dy > 0 ? p.y + next.position.h : next.position.y });
}

/** Move up or down in reading order (DDB-34): the widget takes its neighbour's place and the neighbour makes way. */
export function swap(widgets: DocWidget[], id: string, dir: -1 | 1): DocWidget[] {
  const order = [...widgets].sort((a, b) => a.position.y - b.position.y || a.position.x - b.position.x);
  const i = order.findIndex((w) => w.id === id);
  const me = order[i];
  const other = order[i + dir];
  if (!me || !other) return widgets;
  return place(widgets, id, { ...me.position, x: other.position.x, y: other.position.y });
}

export function resize(widgets: DocWidget[], id: string, dw: number, dh: number): DocWidget[] {
  const w = widgets.find((x) => x.id === id);
  if (!w) return widgets;
  const p = w.position;
  // Growing past the right edge shifts the widget left rather than refusing.
  return place(widgets, id, { x: p.x, y: p.y, w: p.w + dw, h: p.h + dh });
}

/** A widget at a given size (the menu's Size choices and the corner handle). */
export function setSize(widgets: DocWidget[], id: string, size: { w: number; h: number }): DocWidget[] {
  const w = widgets.find((x) => x.id === id);
  return w ? place(widgets, id, { ...w.position, ...size }) : widgets;
}

/**
 * A document as the editor starts from it: every widget at least its type's minimum (an older app may have saved it
 * smaller), then gravity (DDB-30, DDB-32).
 */
export function normalize(widgets: DocWidget[]): DocWidget[] {
  let out = widgets;
  for (const w of widgets) {
    const min = minSize(w.type);
    if (w.position.w < min.w || w.position.h < min.h) out = setSize(out, w.id, w.position);
  }
  return compact(out);
}

/** Removes a widget; gravity closes the gap it leaves (DDB-30). */
export function remove(widgets: DocWidget[], id: string): DocWidget[] {
  if (!widgets.some((w) => w.id === id)) return widgets;
  return compact(widgets.filter((w) => w.id !== id));
}

/** The row below every widget: where a new one goes. */
export function bottom(widgets: DocWidget[]): number {
  return Math.max(0, ...widgets.map((w) => w.position.y + w.position.h));
}

const SOURCE_TYPES = new Set(["map", "status_list", "alarm_list", "health", "weather_station"]);

/**
 * A new dashboard widget for one device from the picker's choice (`toWidget` binds the keys as the device page
 * does, compass roles included), placed at the bottom with its type's default size, under a free id.
 */
export function newWidget(
  widgets: DocWidget[],
  built: { type: string; title?: string; bindings?: DocWidget["bindings"]; config?: Record<string, unknown> },
  device: string,
): DocWidget {
  const taken = new Set(widgets.map((w) => w.id));
  let n = widgets.length + 1;
  while (taken.has(`w${n}`)) n++;
  const size = defaultSize(built.type);
  return {
    id: `w${n}`,
    type: built.type as DocWidget["type"],
    position: { x: 0, y: bottom(widgets), ...size },
    ...(built.title && !built.title.startsWith("i18n:") ? { title: built.title } : {}),
    ...(SOURCE_TYPES.has(built.type)
      ? { source: { devices: [device] as [string] } }
      : built.bindings?.length
        ? { bindings: built.bindings }
        : {}),
    ...(built.config && Object.keys(built.config).length ? { config: built.config } : {}),
  };
}

const CHART_TYPES = new Set(["line_chart", "bar_chart"]);
/** The types that show several values, with how many each takes (dashboard.json): their ⋯ menu lists Values. */
export const MULTI_VALUE: Readonly<Record<string, number>> = {
  line_chart: 8,
  bar_chart: 8,
  value_grid: 12,
  device_tile: 4,
  level: 6,
  state_timeline: 6,
};

/**
 * Change (DDB-39, WPK-04): the widget shows another value, or the same one another way, in place. Its id, title and
 * place stay (grown to the new type's minimum when it is larger); its settings stay only when the type does; a chart
 * that stays a chart keeps its lines from other devices.
 */
export function change(
  widgets: DocWidget[],
  id: string,
  built: { type: string; bindings?: DocWidget["bindings"]; config?: Record<string, unknown> },
  device: string,
): DocWidget[] {
  const w = widgets.find((x) => x.id === id);
  if (!w) return widgets;
  const others =
    CHART_TYPES.has(w.type) && CHART_TYPES.has(built.type) ? (w.bindings ?? []).filter((b) => b.device !== device) : [];
  const bindings = [...(built.bindings ?? []), ...others].slice(0, MULTI_VALUE[built.type]);
  const config = built.type === w.type ? w.config : built.config;
  const next: DocWidget = {
    id: w.id,
    type: built.type as DocWidget["type"],
    position: w.position,
    ...(w.title ? { title: w.title } : {}),
    ...(SOURCE_TYPES.has(built.type)
      ? { source: { devices: [device] as [string] } }
      : bindings.length
        ? { bindings: bindings as DocWidget["bindings"] }
        : {}),
    ...(config && Object.keys(config).length ? { config } : {}),
  };
  return setSize(
    widgets.map((x) => (x.id === id ? next : x)),
    id,
    w.position,
  );
}

/** Values (DDB-39): one value leaves a widget that shows several; the last one stays (Change replaces it). */
export function removeValue(widgets: DocWidget[], id: string, index: number): DocWidget[] {
  return widgets.map((w) => {
    const bindings = w.bindings ?? [];
    if (w.id !== id || bindings.length < 2 || !bindings[index]) return w;
    return { ...w, bindings: bindings.filter((_, i) => i !== index) as DocWidget["bindings"] };
  });
}
